import React from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import DownloadIcon from '@mui/icons-material/Download';

import {
  VpAddToLibraryButton,
  VpAddToPluginButton
} from '@vp-ui/widgets/VpMediaTable/VpMediaSubTable';
import {
  formatMediaVersionLabel,
  getVersionLanguageLabel
} from '@src/helpers/captionVersionHelpers';
import { formatCropLabel } from '@src/constants/crop';
import { useForceDownload } from '@src/hooks';

/**
 * The original is not a version in the backend — it is the media itself. The Publish list shows
 * it as a row so that publishing it is an explicit choice rather than the side effect of a
 * button with no context. This sentinel is how such a row says "no burn": both ids must reach
 * the API as null, which is exactly how the raw media has always been published.
 */
export const ORIGINAL_VERSION_ID = 'original';

const isBranding = (version) => version.kind === 'branding';

/**
 * The id of a version, whichever name it arrived under.
 *
 * Exported because the callers that act on a row — removing a burn, matching the row awaiting
 * confirmation — have to resolve the id exactly as the row did. Two copies of this rule that
 * read the names in a different order agree only for as long as the API keeps emitting both
 * (`BrandingV1Mapper.burnToDto` sends `burnId` and `burnInId` with the same value); the day it
 * stops, the confirm state stops matching and a click either deletes without asking or never
 * confirms at all.
 */
export const versionId = (version) =>
  version.burnInId || version.burnId || version.captionId || null;

const isOriginal = (version) => versionId(version) === ORIGINAL_VERSION_ID;

/**
 * One row per media version, shared by every surface that lists them.
 *
 * The row knows nothing about where the versions came from or what the actions do: callers pass
 * the already-concatenated list and declare which affordances they want. That is what keeps
 * four surfaces — media tab, Caption, Branding and Publish — from drifting into four different
 * rows, which is where this started.
 */
const VpMediaVersionList = ({
  versions,
  mediaId,
  actions,
  onRemove,
  onViewInMediaTab,
  confirmingRemoveId
}) => {
  const { forceDownload } = useForceDownload();
  const shows = (action) => actions.includes(action);

  return (
    <Stack component="ul" gap={1} sx={{ listStyle: 'none', p: 0, m: 0 }}>
      {versions.map((version) => {
        const id = versionId(version);

        // The original row carries its label ready-made; everything else goes through the one
        // shared decision, which the media tab's sub-table uses too.
        const label = isOriginal(version) ? version.label : formatMediaVersionLabel(version);

        const language =
          isOriginal(version) || isBranding(version) ? null : getVersionLanguageLabel(version);
        // Three separate questions, and treating them as one is a silent failure in both
        // directions: an original with no `mediaUrl` lost the only way to publish it, and a
        // caption burn-in past the Glacier transition — `completed`, but with the url withheld —
        // lost the media tab, which is the one place it can be restored from.
        const isCompleted = version.status === 'completed';
        const hasFile = isCompleted && !!version.burnInUrl;
        // Publishing the original sends both ids as null; it never reads the url.
        const canPublish = hasFile || isOriginal(version);
        const isWorking = version.status === 'pending' || version.status === 'processing';
        // The fallback is unreachable today (download needs `hasFile`, which needs the url), but
        // the label carries a date with slashes in it — sanitised so a future change to the gate
        // cannot produce a filename with a path separator in the middle.
        const fileName = version.burnInUrl
          ? version.burnInUrl.split('/').pop().split('?')[0]
          : `${label.replace(/[^\w.\- ]+/g, '_')}.mp4`;

        return (
          <Stack
            key={id}
            component="li"
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
          >
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Chip
                label={label}
                size="small"
                variant="outlined"
                color={version.status === 'failed' ? 'error' : 'default'}
              />
              {language && <Chip label={language} size="small" variant="outlined" />}
              {/* Caption versions carry the settings they were burned with. Driven by data
                  presence rather than by `kind`, so a version without them simply shows less. */}
              {version.preset && (
                <Typography variant="caption" color="text.secondary">
                  {version.preset} · {formatCropLabel(version.crop)}
                  {version.highlightMode ? ` · ${version.highlightMode}` : ''}
                </Typography>
              )}
              {isWorking && <CircularProgress size={14} />}
              {/* The message belongs to the row, not to the panel: with several burns listed, a
                  single alert on top no longer says which one failed. */}
              {version.status === 'failed' && version.errorMessage && (
                <Typography variant="caption" color="error">
                  {version.errorMessage}
                </Typography>
              )}
            </Stack>

            <Stack direction="row" alignItems="center" gap={0.5} flexShrink={0}>
              {shows('download') && hasFile && (
                <Tooltip title="Download">
                  <IconButton
                    size="small"
                    aria-label={`Download ${label}`}
                    onClick={() => forceDownload(version.burnInUrl, fileName)}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {shows('library') && canPublish && (
                <VpAddToLibraryButton
                  mediaId={mediaId}
                  burnInId={isOriginal(version) || isBranding(version) ? null : id}
                  brandingBurnId={!isOriginal(version) && isBranding(version) ? id : null}
                />
              )}
              {shows('plugin') && canPublish && (
                <VpAddToPluginButton
                  mediaId={mediaId}
                  burnInId={isOriginal(version) || isBranding(version) ? null : id}
                  brandingBurnId={!isOriginal(version) && isBranding(version) ? id : null}
                />
              )}
              {shows('viewInMediaTab') && isCompleted && (
                <Button size="small" variant="text" onClick={() => onViewInMediaTab(version)}>
                  View in Media Tab
                </Button>
              )}
              {/* Not while it runs: deleting a burn mid-flight leaves the lambda writing to S3
                  for a row that no longer exists. A failed one can still be cleaned up. */}
              {shows('remove') && !isWorking && (
                <Button
                  size="small"
                  variant="text"
                  color="error"
                  onClick={() => onRemove(version)}
                >
                  {confirmingRemoveId === id ? 'Confirm removal' : 'Remove'}
                </Button>
              )}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};

VpMediaVersionList.propTypes = {
  versions: PropTypes.array.isRequired,
  mediaId: PropTypes.string,
  actions: PropTypes.arrayOf(PropTypes.string).isRequired,
  onRemove: PropTypes.func,
  onViewInMediaTab: PropTypes.func,
  confirmingRemoveId: PropTypes.string
};

VpMediaVersionList.defaultProps = {
  mediaId: null,
  onRemove: () => {},
  onViewInMediaTab: () => {},
  confirmingRemoveId: null
};

export default VpMediaVersionList;



// WEBPACK FOOTER //
// ./src/vp-ui/components/VpMediaVersionList/VpMediaVersionList.jsx